// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Typography, Radio, Alert } from '@mui/material';
import { useProfilingData } from '@shared/advisory/client-profile/providers';
import { AppIcon, Input, Label, Table } from '@shared/ui-kit/components';
import { DECIMAL_SEPARATORS } from '@shared/ui-kit/utils/constants';
import { Translation } from '@shared/ui-kit/providers';
import {
  type ChangeEvent,
  useCallback,
  useMemo,
  useState,
  type FC,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';

export const CommissionStep: FC = () => {
  const { t } = useTranslation();
  const { data, updateAdvisoryData, updateStepValidity } = useProfilingData();

  const [customValue, setCustomValue] = useState<number | undefined>(() =>
    data.commission_id === 'custom'
      ? Number((data.commission_managed_assets * 100).toFixed(2))
      : undefined,
  );

  const commissionOptions = useMemo(
    () => [
      {
        id: 'no-commission',
        name: t('activation-step4.commission-options.no-commission.name', {
          ns: 'components',
        }) as string,
        value: 0,
      },
      {
        id: '0.3',
        name: t('activation-step4.commission-options.0.3.name', {
          ns: 'components',
        }) as string,
        value: 0.003,
      },
      {
        id: '0.5',
        name: t('activation-step4.commission-options.0.5.name', {
          ns: 'components',
        }) as string,
        value: 0.005,
      },
      {
        id: '0.75',
        name: t('activation-step4.commission-options.0.75.name', {
          ns: 'components',
        }) as string,
        value: 0.0075,
      },
      {
        id: '1.0',
        name: t('activation-step4.commission-options.1.0.name', {
          ns: 'components',
        }) as string,
        value: 0.01,
      },
      {
        id: 'custom',
        name: t('activation-step4.custom-option.name', {
          ns: 'components',
          value: customValue,
        }) as string,
        value: (customValue ?? 0) / 100,
      },
    ],
    [t, customValue],
  );

  const [selectedValue, setSelectedValue] = useState(data.commission_id);

  const commissionOption = useMemo(() => {
    return commissionOptions.find(type => type.id === selectedValue);
  }, [commissionOptions, selectedValue]);

  /* istanbul ignore next */
  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
  }, []);

  useEffect(() => {
    updateStepValidity('commission', selectedValue !== '');

    updateAdvisoryData({
      commission_managed_assets: commissionOption?.value,
      commission_id: selectedValue,
      commission_label: commissionOption?.name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commissionOption?.name, commissionOption?.value, selectedValue]);

  return (
    <form className="flex flex-col gap-y-4" data-testid="commission-step-form">
      <div className="flex flex-col space-y-2">
        <Typography variant="h5" data-testid="title">
          <Translation i18nKey="activation-step4.title" ns="components" />
        </Typography>

        <Typography variant="body2" data-testid="description">
          <Translation i18nKey="activation-step4.description" ns="components" />
        </Typography>
      </div>

      <div className="flex flex-col gap-2">
        {Array.from(
          commissionOptions,
          (option, key) =>
            option.id !== 'custom' && (
              <div
                key={key}
                className="flex items-center space-x-2 rounded-md border border-gray-200 p-2 pr-4 transition-colors hover:bg-gray-50"
              >
                <Radio
                  id={option.id}
                  data-testid={`radio-${option.id}`}
                  name="retrocession"
                  checked={selectedValue === option.id}
                  onChange={handleChange}
                  value={option.id}
                />
                <Label
                  htmlFor={option.id}
                  className="flex flex-1 cursor-pointer  items-center justify-between text-gray-700"
                  data-testid={`label-${option.id}`}
                >
                  <div className="flex items-center gap-x-2">
                    <span>
                      <AppIcon icon="Percent" className="size-5" color="main" />
                    </span>
                    <span>{option.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-primary">
                    <Table.Data
                      format="percentage"
                      value={option.value}
                      formatOptions={{
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }}
                    />
                  </span>
                </Label>
              </div>
            ),
        )}
      </div>

      <div className="flex flex-col gap-2 rounded-lg bg-section p-5">
        <fieldset className="flex flex-col gap-3">
          <Label>
            <Translation
              i18nKey="activation-step4.manual-input.label"
              ns="components"
            />
          </Label>
          <NumericFormat
            type="text"
            suffix="%"
            value={customValue}
            allowNegative={false}
            decimalScale={2}
            allowLeadingZeros={false}
            allowedDecimalSeparators={DECIMAL_SEPARATORS}
            isAllowed={({ floatValue }) => {
              if (!floatValue) return true;

              return floatValue >= 0 && floatValue <= 2.5;
            }}
            customInput={Input}
            placeholder={
              t('activation-step4.manual-input.placeholder', {
                ns: 'components',
              }) as string
            }
            onValueChange={({ floatValue }) => setCustomValue(floatValue ?? 0)}
            onInput={() => setSelectedValue('custom')}
            autoFocus={selectedValue === 'custom'}
            className="money-display"
          />
          <output className="text-sm text-gray-500">
            <Translation
              i18nKey="activation-step4.manual-input.helper"
              ns="components"
            />
          </output>
        </fieldset>
      </div>

      {commissionOption && (
        <Alert
          severity="info"
          icon={false}
          className="!bg-alert-info-bg text-alert-info-text [&_.MuiAlert-message]:!py-0"
          data-testid="commission-alert"
        >
          <Translation
            i18nKey="activation-step4.selected-commission"
            ns="components"
            values={{ option: commissionOption?.name }}
          />
        </Alert>
      )}
    </form>
  );
};
